import { useEffect, useState, type RefObject } from 'react'

import {
  setProductImage,
  showSample,
} from '~/widgets/CostBomDemo/engine/heroEngine'
import { useHeroPhase } from '~/widgets/CostBomDemo/hooks/useHeroPhase'
import { SAMPLE_PRODUCTS } from '~/widgets/CostBomDemo/strings'

type Sample = (typeof SAMPLE_PRODUCTS)[number]

interface HeroSampleTabsProps {
  heroRef: RefObject<HTMLElement | null>
}

/**
 * The prepared examples, as tabs on the model itself.
 *
 * They belong here rather than in the composer: the composer is where someone
 * describes their own product, and these are models we already built. Putting
 * them on the result says what they are, and costs nothing to switch between,
 * since every panel is pre-rendered.
 *
 * They stay out of the way until the first model has finished building. Offering
 * a choice of examples while one is still being made asks a visitor to pick
 * before they have seen what picking gets them.
 */
export function HeroSampleTabs({ heroRef }: HeroSampleTabsProps) {
  const [activeKey, setActiveKey] = useState<Sample['key']>(
    SAMPLE_PRODUCTS[0].key,
  )
  const phase = useHeroPhase(heroRef)
  // Once shown they stay, whatever the engine does next.
  const [revealed, setRevealed] = useState(false)
  useEffect(() => {
    if (phase === 'ready') setRevealed(true)
  }, [phase])

  const show = (sample: Sample) => {
    setActiveKey(sample.key)
    setProductImage(sample.product)
    if (heroRef.current) showSample(heroRef.current, sample.key)
  }

  if (!revealed) return null

  return (
    <div className="hero-sample-tabs" role="tablist">
      {SAMPLE_PRODUCTS.map((sample) => (
        <button
          key={sample.key}
          type="button"
          role="tab"
          aria-selected={activeKey === sample.key}
          className={`hero-sample-tab${
            activeKey === sample.key ? ' is-active' : ''
          }`}
          onClick={() => show(sample)}
        >
          {sample.product}
        </button>
      ))}
    </div>
  )
}
